import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Menu, X, ShoppingBag, ArrowUp, Home, Package, Store, BookOpen, MessageSquare, Crown, FlaskConical, HelpCircle, Mail, ClipboardList, Truck, Gift, LayoutDashboard, LifeBuoy, Bitcoin } from "lucide-react";
import { useCart } from "@/cart/CartContext";
import { useAuth } from "@/auth/AuthContext";
import { useContent } from "@/hooks/useContent";

const NAV = [
  { to: "/products", label: "Products" },
  { to: "/vendors", label: "Marketplace" },
  { to: "/library", label: "Certificate of Analysis" },
  { to: "/discussions", label: "The WardRoom" },
  { to: "/membership", label: "Membership" },
  { to: "/track", label: "Track" },
  { to: "/support", label: "Support" },
  { to: "/affiliate", label: "Affiliates" },
  { to: "/crypto-briefing", label: "Crypto Briefing" },
  { to: "/faq", label: "FAQ" },
];

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const NAV_ICONS = {
  Home, Products: Package, Marketplace: Store, "Certificate of Analysis": BookOpen,
  "The WardRoom": MessageSquare, Membership: Crown, FAQ: HelpCircle,
  Contact: Mail, "My Orders": ClipboardList, Track: Truck, Affiliates: Gift, Support: LifeBuoy, "Crypto Briefing": Bitcoin,
};

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const { count } = useCart();
  const { user, logout } = useAuth();
  // Admin may be signed in via the dashboard's own session (separate from main auth).
  const adminSession = typeof sessionStorage !== "undefined" && Boolean(sessionStorage.getItem("vf-admin-token"));
  const showDashboard = user?.role === "admin" || adminSession;
  const showVendorPortal = user?.role === "vendor";
  const content = useContent();
  const DISCLAIMER = content?.footer?.disclaimer ?? "All products sold by VetForge Health and Wellness are intended strictly for laboratory research purposes only. Not for human or veterinary consumption. Not FDA approved. Statements on this site have not been evaluated by the Food and Drug Administration.";

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-cream">
      <div className="grain-overlay" aria-hidden="true" />

      <header
        data-testid="site-header"
        className="sticky top-0 z-50 backdrop-blur-2xl bg-cream/80 border-b border-[#232B40]"
      >
        <div className="mx-auto max-w-7xl px-6 sm:px-10 flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <span className="font-stencil text-lg leading-none text-[#0B101C] bg-clinical px-2.5 py-1.5 rounded-md transition-transform duration-300 group-hover:-rotate-6 shadow-[0_0_18px_rgba(200,169,106,0.35)]">VF</span>
            <span className="font-serif text-2xl tracking-tight text-ink">VetForge</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-[#8E96AB] mt-1">Health &amp; Wellness</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-4" data-testid="nav-desktop">
            {/* Affiliates + FAQ live in the footer and mobile drawer — the desktop bar was too crowded */}
            {NAV.filter((item) => !["/affiliate", "/faq"].includes(item.to)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${slug(item.label)}`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? "text-clinical" : "text-[#B9BFCE] hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {user && user.role !== "admin" && (
              <NavLink
                to="/orders"
                data-testid="nav-link-orders"
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? "text-clinical" : "text-[#B9BFCE] hover:text-ink"
                  }`
                }
              >
                My orders
              </NavLink>
            )}
            {user && user.role === "affiliate" && (
              <NavLink
                to="/affiliate/bank"
                data-testid="nav-link-affiliate-bank"
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? "text-clinical" : "text-[#B9BFCE] hover:text-ink"
                  }`
                }
              >
                Affiliate bank
              </NavLink>
            )}
            {showVendorPortal && (
              <NavLink
                to="/vendor"
                data-testid="nav-link-vendor-portal"
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors duration-300 ${
                    isActive ? "text-clinical" : "text-clinical/70 hover:text-clinical"
                  }`
                }
              >
                Vendor portal
              </NavLink>
            )}
            {showDashboard && (
              <NavLink
                to="/admin"
                data-testid="nav-link-dashboard"
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors duration-300 ${
                    isActive ? "text-clinical" : "text-clinical/70 hover:text-clinical"
                  }`
                }
              >
                Dashboard
              </NavLink>
            )}
          </nav>

          {user && user.role !== "admin" && (
            <span className="hidden xl:flex items-center gap-4">
              <span className="text-xs uppercase tracking-[0.15em] text-[#8E96AB]" data-testid="nav-user-name">
                {user.name.split(" ")[0]}
              </span>
              <button
                data-testid="nav-logout-button"
                onClick={logout}
                className="text-xs uppercase tracking-[0.15em] text-[#8E96AB] hover:text-[#EF4444] transition-colors duration-300"
              >
                Sign out
              </button>
            </span>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart-link"
            className="relative p-2 text-ink hover:text-clinical transition-colors duration-300"
            aria-label="Cart"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 bg-clinical text-cream text-[10px] w-4 h-4 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden text-[#B9BFCE] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

      </header>

      {/* Mobile drawer — rendered outside the header because backdrop-blur on the header
          creates a containing block that would trap position:fixed children */}
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-[#05070D]/70 backdrop-blur-sm xl:hidden" onClick={() => setOpen(false)} aria-hidden="true" />
          <nav
            className="fixed top-0 right-0 bottom-0 z-50 w-72 bg-[#0D1322] border-l border-[#232B40] px-6 py-6 flex flex-col gap-1.5 overflow-y-auto xl:hidden"
            data-testid="nav-mobile"
          >
            <button
              data-testid="nav-mobile-close"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="self-end text-[#8E96AB] hover:text-clinical transition-colors duration-300 mb-4 p-1"
            >
              <X className="h-6 w-6" />
            </button>
            <NavLink
              to="/"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-link-home"
              className={({ isActive }) =>
                `flex items-center gap-4 rounded-xl px-4 py-3.5 text-sm transition-colors duration-300 ${
                  isActive ? "text-clinical bg-clinical/10" : "text-[#B9BFCE] hover:text-ink hover:bg-surface"
                }`
              }
            >
              <Home className="h-5 w-5 shrink-0" strokeWidth={1.5} />
              Home
            </NavLink>
            {NAV.map((item) => {
              const Icon = NAV_ICONS[item.label] || Store;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${slug(item.label)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-4 rounded-xl px-4 py-3.5 text-sm transition-colors duration-300 ${
                      isActive ? "text-clinical bg-clinical/10" : "text-[#B9BFCE] hover:text-ink hover:bg-surface"
                    }`
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              );
            })}
            {(user || adminSession) && (
              <div className="mt-3 pt-3 border-t border-[#232B40] space-y-1.5">
                {user && user.role !== "admin" && (
                  <NavLink
                    to="/orders"
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-link-orders"
                    className={({ isActive }) =>
                      `flex items-center gap-4 rounded-xl px-4 py-3.5 text-sm transition-colors duration-300 ${
                        isActive ? "text-clinical bg-clinical/10" : "text-[#B9BFCE] hover:text-ink hover:bg-surface"
                      }`
                    }
                  >
                    <ClipboardList className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    My orders
                  </NavLink>
                )}
                {showVendorPortal && (
                  <NavLink
                    to="/vendor"
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-link-vendor-portal"
                    className={({ isActive }) =>
                      `flex items-center gap-4 rounded-xl px-4 py-3.5 text-sm font-medium transition-colors duration-300 ${
                        isActive ? "text-clinical bg-clinical/10" : "text-clinical/80 hover:text-clinical hover:bg-clinical/5"
                      }`
                    }
                  >
                    <Store className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    Vendor portal
                  </NavLink>
                )}
                {showDashboard && (
                  <NavLink
                    to="/admin"
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-link-dashboard"
                    className={({ isActive }) =>
                      `flex items-center gap-4 rounded-xl px-4 py-3.5 text-sm font-medium transition-colors duration-300 ${
                        isActive ? "text-clinical bg-clinical/10" : "text-clinical/80 hover:text-clinical hover:bg-clinical/5"
                      }`
                    }
                  >
                    <LayoutDashboard className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    Dashboard
                  </NavLink>
                )}
              </div>
            )}
          </nav>
        </>
      )}

      <main>{children}</main>

      {showTop && (
        <button
          data-testid="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-50 bg-clinical text-[#0B101C] p-3.5 border border-[rgba(200,169,106,0.28)] shadow-lg hover:bg-[#DBC28C] transition-colors duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}

      <div className="border-y border-[#232B40] py-5 overflow-hidden" data-testid="compliance-marquee">
        <Marquee speed={28} gradient={false} aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="mx-12 text-xs uppercase tracking-[0.35em] text-warning whitespace-nowrap">
              Research use only — Not for human consumption
            </span>
          ))}
        </Marquee>
      </div>

      <footer data-testid="site-footer" className="border-t border-[#232B40]">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-16">
          <div className="space-y-5">
            <p className="font-serif text-3xl text-ink">VetForge</p>
            <p className="text-sm text-[#8E96AB] leading-relaxed max-w-xs">
              Research-grade peptides and pharmaceutical diluents, independently verified and delivered with full documentation.
            </p>
          </div>
          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8E96AB]">Navigate</p>
            <div className="flex flex-col gap-3">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`footer-link-${slug(item.label)}`}
                  className="text-sm text-[#B9BFCE] hover:text-clinical transition-colors duration-300 w-fit"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8E96AB]">Legal</p>
            <p data-testid="footer-disclaimer" className="text-xs uppercase tracking-[0.15em] text-warning leading-loose">
              {DISCLAIMER}
            </p>
            <div className="flex flex-col gap-3 pt-2">
              <Link
                to="/terms"
                data-testid="footer-link-terms"
                className="text-sm text-[#B9BFCE] hover:text-clinical transition-colors duration-300 w-fit"
              >
                Terms of Service
              </Link>
              <Link
                to="/privacy"
                data-testid="footer-link-privacy"
                className="text-sm text-[#B9BFCE] hover:text-clinical transition-colors duration-300 w-fit"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#232B40] py-6">
          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-[#8E96AB]">
            © 2026 VetForge Health and Wellness — All rights reserved
          </p>
        </div>
      </footer>
    </div>
  );
}
